import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileText, Download, Building, Calendar, DollarSign,
  CheckCircle, Clock, XCircle, AlertTriangle, Package
} from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { gastosApi } from '../../utils/api';

const estadoConfig = {
  por_confirmar: { label: 'Por Confirmar', color: 'bg-orange-100 text-orange-800', icon: Clock },
  procesando_hacienda: { label: 'Procesando', color: 'bg-blue-100 text-blue-800', icon: Clock },
  procesando_rechazo: { label: 'Rechazando', color: 'bg-red-100 text-red-800', icon: Clock },
  aceptada: { label: 'Aceptada', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rechazada: { label: 'Rechazada', color: 'bg-red-100 text-red-800', icon: XCircle },
  por_pagar: { label: 'Por Pagar', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  parcial: { label: 'Parcial', color: 'bg-purple-100 text-purple-800', icon: DollarSign },
  pagado: { label: 'Pagado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  anulado: { label: 'Anulado', color: 'bg-gray-100 text-gray-800', icon: XCircle }
};

const haciendaEstadoConfig = {
  aceptado: { label: 'Aceptado por Hacienda', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  aceptado_parcial: { label: 'Aceptado Parcial', color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
  rechazado: { label: 'Rechazado por Hacienda', color: 'bg-red-100 text-red-800', icon: XCircle }
};

const condicionVentaConfig = {
  '01': { label: 'Contado', color: 'text-green-600' },
  '02': { label: 'Crédito', color: 'text-blue-600' },
  '03': { label: 'Consignación', color: 'text-purple-600' },
  '04': { label: 'Apartado', color: 'text-orange-600' },
  '05': { label: 'Arrendamiento opción compra', color: 'text-gray-600' },
  '06': { label: 'Arrendamiento función financiera', color: 'text-gray-600' },
  '07': { label: 'Cobro a favor tercero', color: 'text-gray-600' },
  '08': { label: 'Servicios prestados al Estado', color: 'text-gray-600' },
  '09': { label: 'Pago del servicios prestado al Estado', color: 'text-gray-600' },
  '99': { label: 'Otros', color: 'text-gray-600' }
};

export default function DetalleFactura() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [factura, setFactura] = useState(null);
  const [lineas, setLineas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFactura();
  }, [id]);

  const fetchFactura = async () => {
    try {
      setLoading(true);
      const response = await gastosApi.getFactura(id);
      setFactura(response.data.data);
      setLineas(response.data.data.lineas || []);
    } catch (error) {
      console.error('Error fetching factura:', error);
      toast.error('Error al cargar la factura');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount, currency = 'CRC') => {
    const num = parseFloat(amount) || 0;
    if (currency === 'USD') {
      return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `₡${num.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleDownloadXML = async () => {
    try {
      const response = await gastosApi.downloadXML(id);
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura-${factura.numeroConsecutivo}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al descargar XML');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!factura) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Factura no encontrada</p>
      </div>
    );
  }

  const estado = estadoConfig[factura.estado] || estadoConfig.por_confirmar;
  const haciendaEstado = factura.haciendaEstadoOriginal ? haciendaEstadoConfig[factura.haciendaEstadoOriginal] : null;
  const isVencida = factura.fechaVencimiento &&
    dayjs(factura.fechaVencimiento).isBefore(dayjs()) &&
    factura.estado !== 'pagado' && factura.estado !== 'anulado';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            Factura {factura.numeroConsecutivo}
          </h1>
          <p className="text-sm text-gray-500">
            {factura.tipoDocumento} - {factura.proveedorNombre}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${estado.color}`}>
            <estado.icon size={16} />
            {estado.label}
          </span>
          {factura.clave && (
            <button
              onClick={handleDownloadXML}
              className="flex items-center gap-2 px-3 py-1.5 border rounded-lg hover:bg-gray-50"
            >
              <Download size={16} />
              XML
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Proveedor Card */}
          <div className="bg-white rounded-lg border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building className="text-blue-600" size={20} />
              </div>
              <h2 className="text-lg font-semibold">Proveedor</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Nombre</p>
                <p className="font-medium">{factura.proveedorNombre}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Identificacion</p>
                <p className="font-medium">{factura.proveedorIdentificacion}</p>
              </div>
              {factura.proveedorEmail && (
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{factura.proveedorEmail}</p>
                </div>
              )}
            </div>
          </div>

          {/* Lineas de Detalle */}
          <div className="bg-white rounded-lg border">
            <div className="px-6 py-4 border-b">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Package className="text-purple-600" size={20} />
                </div>
                <h2 className="text-lg font-semibold">Detalle de Lineas</h2>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Detalle</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Cant</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Precio</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">IVA</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {lineas.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                        Sin lineas de detalle
                      </td>
                    </tr>
                  ) : (
                    lineas.map((linea, idx) => (
                      <tr key={linea.id || idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-500">{linea.numeroLinea || idx + 1}</td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-900">{linea.detalle}</p>
                          {linea.codigo && (
                            <p className="text-xs text-gray-500">Cod: {linea.codigo}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          {linea.cantidad} {linea.unidadMedida}
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          {formatMoney(linea.precioUnitario, factura.moneda)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          {linea.impuestoTarifa ? `${linea.impuestoTarifa}%` : '-'}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium">
                          {formatMoney(linea.montoTotalLinea, factura.moneda)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Clave Electronica */}
          {factura.clave && (
            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FileText className="text-green-600" size={20} />
                </div>
                <h2 className="text-lg font-semibold">Clave Electronica</h2>
              </div>
              <p className="font-mono text-sm bg-gray-50 p-3 rounded break-all">
                {factura.clave}
              </p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Documento */}
          <div className="bg-white rounded-lg border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Calendar className="text-orange-600" size={20} />
              </div>
              <h2 className="text-lg font-semibold">Documento</h2>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Fecha:</span>
                <span className="text-sm font-medium">
                  {dayjs(factura.fechaEmision).format('DD/MM/YYYY HH:mm')}
                </span>
              </div>
              {factura.fechaVencimiento && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Vencimiento:</span>
                  <span className={`text-sm font-medium ${isVencida ? 'text-red-600' : ''}`}>
                    {dayjs(factura.fechaVencimiento).format('DD/MM/YYYY')}
                    {isVencida && ' (Vencida)'}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Condicion:</span>
                <span className={`text-sm font-semibold ${condicionVentaConfig[factura.condicionVenta]?.color || 'text-gray-600'}`}>
                  {condicionVentaConfig[factura.condicionVenta]?.label || 'No especificada'}
                  {factura.plazoCredito && factura.condicionVenta === '02' && ` (${factura.plazoCredito} días)`}
                </span>
              </div>
            </div>
          </div>

          {/* Totales */}
          <div className="bg-white rounded-lg border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="text-green-600" size={20} />
              </div>
              <h2 className="text-lg font-semibold">Totales</h2>
            </div>
            <div className="space-y-3">
              {factura.moneda !== 'CRC' && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Tipo Cambio:</span>
                  <span>{factura.tipoCambio}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal Gravado:</span>
                <span>{formatMoney(factura.totalGravado, factura.moneda)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal Exento:</span>
                <span>{formatMoney(factura.totalExento, factura.moneda)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">IVA:</span>
                <span>{formatMoney(factura.totalImpuesto, factura.moneda)}</span>
              </div>
              <div className="border-t pt-3 flex justify-between">
                <span className="font-semibold">Total:</span>
                <span className="font-bold text-lg">
                  {formatMoney(factura.totalComprobante, factura.moneda)}
                </span>
              </div>
              {factura.estado !== 'pagado' && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Pagado:</span>
                    <span className="text-green-600">{formatMoney(factura.totalPagado, factura.moneda)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-red-600">Saldo:</span>
                    <span className="font-bold text-red-600">
                      {formatMoney(factura.saldoPendiente, factura.moneda)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Estado Hacienda Original */}
          {haciendaEstado && (
            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <CheckCircle className="text-blue-600" size={20} />
                </div>
                <h2 className="text-lg font-semibold">Hacienda (Proveedor)</h2>
              </div>
              <div className="space-y-3">
                <div className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${haciendaEstado.color}`}>
                  <haciendaEstado.icon size={16} />
                  {haciendaEstado.label}
                </div>
                {factura.haciendaMensaje && (
                  <p className="text-sm text-gray-600 mt-2">
                    {factura.haciendaMensaje}
                  </p>
                )}
                <p className="text-xs text-gray-400">
                  Estado original cuando el proveedor emitio la factura
                </p>
              </div>
            </div>
          )}

          {/* Notas */}
          {factura.notas && (
            <div className="bg-white rounded-lg border p-6">
              <h2 className="text-lg font-semibold mb-3">Notas</h2>
              <p className="text-sm text-gray-600">{factura.notas}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
